// ============================================================================
// Systèmes d'équations linéaires sur la feuille du noyau (moteur v22, refonte
// lot 5 — docs/moteur/REFONTE_MOTEUR.md).
// ============================================================================
// Bookkeeping PUR, sans message ni HORA (ceux-ci vivent dans kernel-sheet.ts) :
//
//   - une ligne est JUSTE si elle découle des équations de l'énoncé : sa forme est
//     une combinaison linéaire exacte d'un sous-ensemble COMPATIBLE des équations
//     (`kernel/system.ts`). Seule exception : une contradiction (« 0 = 1 ») découle
//     de tout le système quand il est impossible — c'est même la réponse attendue ;
//   - un pas = UN geste du cours, retrouvé depuis les lignes de confiance (énoncé,
//     puis lignes validées) :
//       · transformer UNE ligne (équation proportionnelle : isoler, simplifier) ;
//       · remplacer une inconnue isolée (« x = 1 + y ») dans une autre ligne ;
//       · combiner DEUX lignes (a·L₁ + b·L₂, a et b entiers non nuls) pour éliminer
//         une inconnue ;
//       · constater que deux lignes venues d'équations différentes disent la même
//         chose (« 0 = 0 », ou retrouver l'autre équation de l'énoncé) ;
//   - chaque ligne garde l'ensemble des équations dont elle vient (`origin`) : c'est
//     ce qui distingue « recopier l'énoncé » de « montrer que L₂ = 2 × L₁ » ;
//   - le but dépend du nombre de solutions annoncé par le modèle :
//       unique   → une ligne « inconnue = nombre » pour chaque inconnue ;
//       none     → une contradiction calculée (« 0 = 1 ») ;
//       infinite → autant de redondances montrées que d'équations en trop
//                  (m − rang), ou, sans équation en trop, une écriture des
//                  inconnues principales en fonction des autres.
// ============================================================================

import * as Q from './kernel/exact';
import type { Rat } from './kernel/exact';
import {
  combinationOf,
  formUnknowns,
  isContradictionForm,
  isTrivialForm,
  linearForm,
  sameEquation,
  solveLinearSystem,
  type LinearForm,
} from './kernel/system';
import { substitute, symbolsOf, type Statement, type Term } from './kernel/term';

export type SystemOutcome = 'unique' | 'none' | 'infinite';

export interface SysLine {
  s: Statement;
  f: LinearForm;
  /** équations de l'énoncé dont la ligne découle (bits : équation i → 1 << i) */
  origin: number;
  /** écriture canonique légère (même ligne écrite pareil) */
  K: string;
  /** la ligne telle qu'elle est écrite (messages) */
  text: string;
  /** « u = e » avec u absent de e : la ligne isole une inconnue */
  isolated: { u: string; e: Term } | null;
}

export interface SystemState {
  unknowns: string[];
  outcome: SystemOutcome;
  equations: SysLine[];
  /** lignes de confiance écrites par l'élève, dans l'ordre */
  lines: SysLine[];
  /** rang du système de l'énoncé, nombre d'équations */
  rank: number;
  /** sous-ensembles compatibles maximaux des équations (masques) */
  compatible: number[];
  /** redondances montrées (paires d'origines disjointes) */
  redundancies: string[];
  /** valeurs établies (« x = 9/5 » calculé) */
  values: Map<string, Rat>;
  /** une contradiction calculée est écrite */
  contradiction: boolean;
}

const bits = (m: number): number[] => {
  const out: number[] = [];
  for (let i = 0; m >> i; i++) if ((m >> i) & 1) out.push(i);
  return out;
};

export function isolatedOf(s: Statement): { u: string; e: Term } | null {
  if (s.k !== 'rel' || s.rel !== '=') return null;
  if (s.l.k === 'sym' && !symbolsOf(s.r).has(s.l.n)) return { u: s.l.n, e: s.r };
  return null;
}

export function makeSysLine(s: Statement, K: string, origin: number, text: string): SysLine | null {
  const f = linearForm(s);
  if (!f) return null;
  return { s, f, origin, K, text, isolated: isolatedOf(s) };
}

export function createSystemState(
  equations: { s: Statement; K: string; text: string }[],
  unknowns: string[],
  outcome: SystemOutcome,
): SystemState {
  const eqs = equations.map((e, i) => {
    const l = makeSysLine(e.s, e.K, 1 << i, e.text);
    if (!l) throw new Error(`équation non linéaire dans le système : ${e.K}`);
    return l;
  });
  const forms = eqs.map((e) => e.f);
  const all = (1 << eqs.length) - 1;
  // sous-ensembles compatibles maximaux (au plus 4 équations : 15 sous-ensembles)
  const ok: number[] = [];
  for (let m = all; m > 0; m--) {
    if (ok.some((o) => (o & m) === m)) continue;
    const sub = bits(m).map((i) => forms[i]!);
    if (solveLinearSystem(sub, unknowns).kind !== 'none') ok.push(m);
  }
  return {
    unknowns,
    outcome,
    equations: eqs,
    lines: [],
    rank: rankOf(forms, unknowns),
    compatible: ok,
    redundancies: [],
    values: new Map(),
    contradiction: false,
  };
}

/** Rang des coefficients (partie en inconnues) d'une famille de formes. */
export function rankOf(forms: readonly LinearForm[], unknowns: readonly string[]): number {
  const sol = solveLinearSystem(
    forms.map((f) => ({ coef: f.coef, c: Q.ZERO })),
    unknowns,
  );
  if (sol.kind === 'unique') return unknowns.length;
  if (sol.kind === 'infinite') return unknowns.length - sol.free.length;
  return 0;
}

/** Système de l'énoncé (inconnues comprises) : solution unique, aucune, infinité ? */
export function systemOutcomeOf(forms: readonly LinearForm[], unknowns: readonly string[]) {
  const s = solveLinearSystem(forms, unknowns);
  return s.kind === 'too_big' ? null : s.kind;
}

/** La ligne découle-t-elle des équations de l'énoncé ? */
export function sysFollows(ss: SystemState, f: LinearForm): boolean {
  const forms = ss.equations.map((e) => e.f);
  if (isContradictionForm(f)) return combinationOf(forms, f) !== null;
  return ss.compatible.some(
    (m) =>
      combinationOf(
        bits(m).map((i) => forms[i]!),
        f,
      ) !== null,
  );
}

/**
 * Les inconnues sont-elles justes et seul le nombre de l'autre membre faux ? Le geste
 * (transformer une ligne, ou combiner deux lignes à coefficients entiers) se retrouve
 * sur la partie en inconnues, pas sur la constante. Renvoie ce geste, sinon null.
 */
export function sysConstantOnlyWrong(
  ss: SystemState,
  f: LinearForm,
): { kind: 'transform' | 'combine'; lines: SysLine[] } | null {
  if (f.coef.size === 0) return null;
  const strip = (g: LinearForm): LinearForm => ({ coef: g.coef, c: Q.ZERO });
  const g = strip(f);
  const src = sysSources(ss);
  const one = src.find((l) => l.f.coef.size > 0 && sameEquation(strip(l.f), g));
  if (one) return { kind: 'transform', lines: [one] };
  for (let i = 0; i < src.length; i++)
    for (let j = i + 1; j < src.length; j++) {
      const a = src[i]!;
      const b = src[j]!;
      const l = combinationOf([strip(a.f), strip(b.f)], g);
      if (!l || Q.isZero(l[0]!) || Q.isZero(l[1]!)) continue;
      if (Q.isInteger(l[0]!) && Q.isInteger(l[1]!)) return { kind: 'combine', lines: [a, b] };
    }
  return null;
}

/** La ligne de confiance la plus proche (mêmes inconnues, mêmes coefficients au signe près). */
export function sysClosestSource(ss: SystemState, f: LinearForm): SysLine | null {
  const mine = [...f.coef.keys()];
  let best: { l: SysLine; score: number } | null = null;
  for (const l of sysSources(ss)) {
    if (l.f.coef.size !== mine.length || !mine.every((u) => l.f.coef.has(u))) continue;
    const score = mine.filter((u) => Q.eq(Q.abs(l.f.coef.get(u)!), Q.abs(f.coef.get(u)!))).length;
    if (!best || score > best.score) best = { l, score };
  }
  return best?.l ?? null;
}

/** Lignes de confiance, la plus récente d'abord, l'énoncé en dernier. */
export function sysSources(ss: SystemState): SysLine[] {
  return [...[...ss.lines].reverse(), ...ss.equations];
}

export type SysMove =
  | { kind: 'transform'; from: SysLine }
  | { kind: 'substitute'; sub: SysLine; into: SysLine; literal: boolean }
  | {
      kind: 'combine';
      a: SysLine;
      b: SysLine;
      lambdas: [Rat, Rat];
      integer: boolean;
      eliminated: string[];
    }
  | { kind: 'redundant'; a: SysLine; b: SysLine };

export const unknownsOf = (f: LinearForm): Set<string> => new Set(f.coef.keys());

/**
 * Les gestes qui mènent des lignes de confiance à la ligne écrite (forme f, écriture K),
 * dans l'ordre de préférence : transformer une ligne (chaque ligne proportionnelle,
 * la plus récente d'abord), remplacer (écriture exacte), combiner (entiers, une
 * inconnue éliminée), puis les gestes « trop d'un coup ». Le juge garde le premier
 * qui fait un pas.
 */
export function findSysMoves(
  ss: SystemState,
  s: Statement,
  f: LinearForm,
  K: string,
  keyOf: (s: Statement) => string,
): SysMove[] {
  const src = sysSources(ss);
  if (isTrivialForm(f)) {
    const out: SysMove[] = [];
    for (let i = 0; i < src.length; i++)
      for (let j = i + 1; j < src.length; j++) {
        const a = src[i]!;
        const b = src[j]!;
        if (
          a.origin !== b.origin &&
          sameEquation(a.f, b.f) &&
          !ss.redundancies.includes(redundancyKey(a.origin, b.origin))
        )
          out.push({ kind: 'redundant', a, b });
      }
    return out;
  }
  const transforms: SysMove[] = src
    .filter((l) => sameEquation(l.f, f))
    .map((from) => ({ kind: 'transform', from }));
  const literal: SysMove[] = [];
  const loose: SysMove[] = [];
  for (const sub of src) {
    if (!sub.isolated) continue;
    const { u, e } = sub.isolated;
    for (const into of src) {
      if (into === sub || !into.f.coef.has(u)) continue;
      if (into.s.k !== 'rel') continue;
      const map = { [u]: e };
      const g: Statement = {
        k: 'rel',
        rel: into.s.rel,
        l: substitute(into.s.l, map),
        r: substitute(into.s.r, map),
      };
      if (keyOf(g) === K) literal.push({ kind: 'substitute', sub, into, literal: true });
      else {
        const gf = linearForm(g);
        if (gf && sameEquation(gf, f))
          loose.push({ kind: 'substitute', sub, into, literal: false });
      }
    }
  }
  const combines: SysMove[] = [];
  const others: SysMove[] = [];
  const target = unknownsOf(f);
  for (let i = 0; i < src.length; i++)
    for (let j = i + 1; j < src.length; j++) {
      const a = src[i]!;
      const b = src[j]!;
      if (sameEquation(a.f, b.f)) continue;
      const l = combinationOf([a.f, b.f], f);
      if (!l || Q.isZero(l[0]!) || Q.isZero(l[1]!)) continue;
      const eliminated = [...new Set([...a.f.coef.keys(), ...b.f.coef.keys()])].filter(
        (n) => !target.has(n),
      );
      const integer = Q.isInteger(l[0]!) && Q.isInteger(l[1]!);
      const move: SysMove = {
        kind: 'combine',
        a,
        b,
        lambdas: [l[0]!, l[1]!],
        integer,
        eliminated: eliminated.sort(),
      };
      (integer && eliminated.length ? combines : others).push(move);
    }
  return [...transforms, ...literal, ...combines, ...loose, ...others];
}

/** Origine d'une ligne obtenue par ce geste. */
export function moveOrigin(m: SysMove): number {
  switch (m.kind) {
    case 'transform':
      return m.from.origin;
    case 'substitute':
      return m.sub.origin | m.into.origin;
    case 'combine':
    case 'redundant':
      return m.a.origin | m.b.origin;
  }
}

/**
 * Clé d'une redondance : deux lignes proportionnelles venues de combinaisons
 * DIFFÉRENTES des équations prouvent une relation entre elles (L₂ = 2 × L₁). La clé
 * est l'ensemble des équations en jeu.
 */
export const redundancyKey = (a: number, b: number): string => String(a | b);

/**
 * La ligne montre-t-elle une NOUVELLE redondance ? Elle RÉÉCRIT une ligne déjà là (même
 * écriture) venue d'autres équations : « 2x + 2y = 4 » ÷ 2 redonne « x + y = 2 ».
 * (Une ligne seulement proportionnelle ne montre rien : on le voit avec « 0 = 0 ».)
 * Renvoie sa clé et l'autre ligne.
 */
export function redundancyOf(ss: SystemState, l: SysLine): { key: string; other: SysLine } | null {
  if (isTrivialForm(l.f)) return null;
  for (const m of sysSources(ss)) {
    if (m.origin === l.origin || m.K !== l.K) continue;
    const key = redundancyKey(m.origin, l.origin);
    if (!ss.redundancies.includes(key)) return { key, other: m };
  }
  return null;
}

/**
 * Transformer une ligne PRÉPARE une combinaison quand un coefficient devient égal
 * (ou opposé) à celui d'une autre ligne : « x + y = 2 » → « 2x + 2y = 4 » face à
 * « 2x + 2y = 5 ».
 */
export function preparesCombination(ss: SystemState, from: SysLine, f: LinearForm): boolean {
  const same = (a: Rat | undefined, b: Rat | undefined): boolean =>
    !!a && !!b && Q.eq(Q.abs(a), Q.abs(b));
  return sysSources(ss).some(
    (o) =>
      !sameEquation(o.f, f) &&
      [...f.coef.keys()].some(
        (u) => same(f.coef.get(u), o.f.coef.get(u)) && !same(from.f.coef.get(u), o.f.coef.get(u)),
      ),
  );
}

export interface SysCommit {
  /** valeur établie : « x = 9/5 », membre droit calculé */
  value?: { u: string; v: Rat };
  /** contradiction calculée (« 0 = 1 ») */
  contradiction?: boolean;
  /** redondance montrée par un « 0 = 0 » (paire d'origines) */
  redundancy?: string;
}

/** Enregistre une ligne de confiance. */
export function commitSysLine(ss: SystemState, l: SysLine, c: SysCommit): void {
  const red = c.redundancy ?? redundancyOf(ss, l)?.key;
  if (red && !ss.redundancies.includes(red)) ss.redundancies.push(red);
  if (c.value) ss.values.set(c.value.u, c.value.v);
  if (c.contradiction) ss.contradiction = true;
  if (!isTrivialForm(l.f)) ss.lines.push(l);
}

/** But atteint (selon le nombre de solutions annoncé) ? */
export function sysGoalReached(ss: SystemState): boolean {
  switch (ss.outcome) {
    case 'unique':
      return ss.unknowns.every((u) => ss.values.has(u));
    case 'none':
      return ss.contradiction;
    case 'infinite': {
      const extra = ss.equations.length - ss.rank;
      if (extra > 0) return ss.redundancies.length >= extra;
      return parametricReached(ss);
    }
  }
}

/** Sans équation en trop : les inconnues principales écrites en fonction des autres. */
function parametricReached(ss: SystemState): boolean {
  const iso = new Map<string, SysLine>();
  for (const l of ss.lines) if (l.isolated && !iso.has(l.isolated.u)) iso.set(l.isolated.u, l);
  const chosen = [...iso.values()];
  // chaque inconnue isolée ne réapparaît dans aucun second membre
  const lead = new Set(iso.keys());
  const clean = chosen.filter((l) => ![...symbolsOf(l.isolated!.e)].some((n) => lead.has(n)));
  if (clean.length < ss.rank) return false;
  return (
    rankOf(
      clean.map((l) => l.f),
      ss.unknowns,
    ) >= ss.rank
  );
}

/** Simule l'ajout d'une ligne : le but serait-il atteint ? */
export function sysWouldReach(ss: SystemState, l: SysLine, c: SysCommit): boolean {
  const copy: SystemState = {
    ...ss,
    lines: [...ss.lines],
    redundancies: [...ss.redundancies],
    values: new Map(ss.values),
  };
  commitSysLine(copy, l, c);
  return sysGoalReached(copy);
}

export { formUnknowns, isContradictionForm, isTrivialForm };
